import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { TrendingUp } from "lucide-react";

const RANGES = [
    { value: 7, label: "7 dage" },
    { value: 30, label: "30 dage" },
    { value: 90, label: "90 dage" },
];

const METRIC_META = {
    trust_score: { label: "Tillidsscore", unit: "%", max: 100 },
    lies_count: { label: "Dokumenterede løgne", unit: "", max: null },
    valgflaesk_count: { label: "Valgflæsk-sager", unit: "", max: null },
};

/**
 * Viser historisk tillidsscore, løgne eller valgflæsk.
 * Hvis `partyId` er givet: kun én linje for det parti.
 * Ellers: alle partier samtidig.
 */
export default function TrustHistoryChart({
    partyId = null,
    defaultDays = 30,
    metric = "trust_score",
    title,
    subtitle,
}) {
    const [data, setData] = useState(null);
    const [singleData, setSingleData] = useState(null);
    const [days, setDays] = useState(defaultDays);
    const [partyMeta, setPartyMeta] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (partyId) {
                const [hist, party] = await Promise.all([
                    api.get(`/parties/${partyId}/history`, { params: { days, metric } }).then((r) => r.data),
                    api.get(`/parties/${partyId}`).then((r) => r.data),
                ]);
                if (!cancelled) {
                    setSingleData(hist.map((s) => ({
                        date: s.date,
                        value: s.value,
                    })));
                    setPartyMeta(party);
                }
            } else {
                const res = await api.get("/stats/history", { params: { days, metric } }).then((r) => r.data);
                if (!cancelled) setData(res);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [partyId, days, metric]);

    const formatDate = (d) => {
        try {
            const dt = new Date(d);
            return dt.toLocaleDateString("da-DK", { day: "2-digit", month: "short" });
        } catch { return d; }
    };

    const meta = METRIC_META[metric] || METRIC_META.trust_score;
    const defaultTitle = partyId
        ? `${meta.label} over tid`
        : `Hvordan har partiernes ${meta.label.toLowerCase()} udviklet sig?`;
    const defaultSubtitle = "Dagligt snapshot — ny måling kl. 12:05 hver dag.";
    const yProps = metric === "trust_score"
        ? { domain: [0, 100], unit: "%" }
        : { allowDecimals: false };

    return (
        <div className="bg-white border border-[#E5E8F0] rounded-2xl p-6 md:p-8" data-testid="trust-history-chart">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-royal" />
                        <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                            {title || "Historisk graf"}
                        </span>
                    </div>
                    <h2 className="font-serif-dm text-2xl md:text-3xl text-royal">
                        {subtitle || defaultTitle}
                    </h2>
                    <p className="text-sm text-[#4F5A7A] mt-1">{defaultSubtitle}</p>
                </div>
                <div className="flex gap-1 self-start md:self-end">
                    {RANGES.map((r) => (
                        <button
                            key={r.value}
                            onClick={() => setDays(r.value)}
                            data-testid={`history-range-${r.value}`}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                                days === r.value
                                    ? "bg-royal text-white"
                                    : "bg-[#F0F2F7] text-[#4F5A7A] hover:bg-[#E5E8F0]"
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            {partyId ? (
                singleData && singleData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={singleData} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid stroke="#EEF0F7" vertical={false} />
                            <XAxis
                                dataKey="date"
                                tickFormatter={formatDate}
                                tick={{ fill: "#4F5A7A", fontSize: 11 }}
                                axisLine={{ stroke: "#D1D6E4" }}
                            />
                            <YAxis
                                {...yProps}
                                tick={{ fill: "#4F5A7A", fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip
                                contentStyle={{ background: "#fff", border: "1px solid #E5E8F0", borderRadius: 10 }}
                                labelFormatter={formatDate}
                                formatter={(v) => [`${v}${meta.unit}`, meta.label]}
                            />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke={partyMeta?.color || "#0C2A6B"}
                                strokeWidth={3}
                                dot={false}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <p className="text-sm text-[#4F5A7A] py-10 text-center">Ingen historik endnu.</p>
                )
            ) : (
                data && data.series && data.series.length > 0 ? (
                    <ResponsiveContainer width="100%" height={420}>
                        <LineChart data={data.series} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid stroke="#EEF0F7" vertical={false} />
                            <XAxis
                                dataKey="date"
                                tickFormatter={formatDate}
                                tick={{ fill: "#4F5A7A", fontSize: 11 }}
                                axisLine={{ stroke: "#D1D6E4" }}
                            />
                            <YAxis
                                {...yProps}
                                tick={{ fill: "#4F5A7A", fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip
                                contentStyle={{ background: "#fff", border: "1px solid #E5E8F0", borderRadius: 10, fontSize: 12 }}
                                labelFormatter={formatDate}
                                formatter={(v, name) => [`${v}${meta.unit}`, name]}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {data.parties.map((p) => (
                                <Line
                                    key={p.id}
                                    type="monotone"
                                    dataKey={p.short_name}
                                    name={p.name}
                                    stroke={p.color}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <p className="text-sm text-[#4F5A7A] py-10 text-center">Indlæser historik...</p>
                )
            )}
        </div>
    );
}
